<!--
  - Copyright (c) 2022 by Leyton Group - All Rights Reserved.
  - This source code, and resulting software, is the confidential and proprietary information
  - ("Proprietary Information") and is the intellectual property ("Intellectual Property")
  - of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
  - and shall use it only in accordance with the terms and conditions of any and all license
  - agreements you have entered into with The Company.
  -->

<template>
  <div id="User">
    <actions
      :teamQuery="true"
      :queryConfig="usersQueryConfig"
      @onUsersFilter="onUsersFilter($event)"
      @reloadData="reloadUsersData($event)"
      @newUserModal="openModalUser"
      :userSearch="userPagedFilter.userSearch"
      @search="updateSearchQuery"
    ></actions>

    <page-loader v-if="loading" :hasActions="true"></page-loader>

    <!-- EMPTY STATE -->
    <empty-state
      v-if="!getUsers.content || !getUsers.content.length"
      module="users"
      title="Commencer à construire votre équipe."
      description="Vous n'avez aucun membre dans votre équipe."
      :addUser="true"
      @newUserModal="openModalUser"
    ></empty-state>

    <div v-if="getUsers.content.length && !loading">
      <b-row v-if="!loading" align-items="stretch">
        <b-col cols="12" xl="12" class="my-4">
          <table class="giga-table" :aria-describedby="$t('users')">
            <thead>
              <tr>
                <th
                  @click="sortUsersTable('firstName')"
                  class="sort width-150-p"
                  scope="col"
                  :class="sortUsersClass('firstName')"
                >
                  {{ $t("user_account") }}
                  <img
                    v-if="sortUsersClass('firstName')"
                    :src="
                      sortUsersClass('firstName') === 'desc'
                        ? descIcon
                        : ascIcon
                    "
                    class="sort-icon"
                    alt="sort-icon"
                  />
                </th>

                <th
                  @click="sortUsersTable('email')"
                  class="sort"
                  scope="col"
                  :class="sortUsersClass('email')"
                >
                  {{ $t("user_email") }}
                  <img
                    v-if="sortUsersClass('email')"
                    :src="
                      sortUsersClass('email') === 'desc' ? descIcon : ascIcon
                    "
                    class="sort-icon"
                    alt="sort-icon"
                  />
                </th>
                <th
                  @click="sortUsersTable('role')"
                  class="sort"
                  scope="col"
                  :class="sortUsersClass('role')"
                >
                  {{ $t("user_groupe_role") }}
                  <img
                    v-if="sortUsersClass('role')"
                    :src="
                      sortUsersClass('role') === 'desc' ? descIcon : ascIcon
                    "
                    class="sort-icon"
                    alt="sort-icon"
                  />
                </th>
                <th scope="col" class="actions-cell">
                  <!-- {{ $t("user_actions") }} -->
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="user in getUsers.content">
                <tr
                  :class="{ inactiveUser: !user.isEnabled }"
                  class="cursor-pointer"
                  :key="user.id"
                >
                  <td
                    class="user-full_name"
                    :data-label="$t('user_first_name')"
                  >
                    <div class="user-thumbnail">
                      <img v-if="user.avatar" :src="user.avatar" alt="avatar" />
                      <img
                        v-else
                        src="@/assets/images/contact-mask.svg"
                        alt="avatar"
                      />
                    </div>
                    <p>{{ user.firstName }} {{ user.lastName }}</p>
                  </td>
                  <td :data-label="$t('user_email')">
                    <p>{{ user.email }}</p>
                  </td>
                  <td :data-label="$t('user_groupe_role')">
                    {{ user.admin ? "ADMIN" : user.role ? user.role.name : "" }}
                  </td>
                  <td :data-label="$t('user_actions')">
                    <ul class="user-actions">
                      <li class="user-action_edit">
                        <button @click="openModalModifUser(user)">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M0 12.8538V15.5556C0 15.8045 0.195528 16 0.444383 16H3.14623C3.26177 16 3.37731 15.9556 3.4573 15.8667L13.1626 6.17025L9.82975 2.83738L0.133315 12.5338C0.0444383 12.6227 0 12.7293 0 12.8538ZM15.74 3.59283C16.0867 3.24622 16.0867 2.68629 15.74 2.33968L13.6603 0.259964C13.3137 -0.0866546 12.7538 -0.0866546 12.4072 0.259964L10.7807 1.8864L14.1136 5.21928L15.74 3.59283Z"
                              fill="white"
                            />
                          </svg>
                        </button>
                      </li>
                      <li
                        v-if="getActivateUserLoader && user.shouldLoading"
                        class="user-action_delete"
                      >
                        <b-spinner
                          style="width: 1.5rem; height: 1.5rem;"
                          variant="primary"
                          class="mr-2 align-middle"
                        >
                        </b-spinner>
                      </li>
                      <li v-else class="user-action_delete">
                        <div
                          v-if="
                            user.hasPassword && userInfo.email !== user.email
                          "
                        >
                          <button
                            v-if="user.isEnabled"
                            @click="deactivateUser(user, null)"
                            data-toggle="tooltip"
                            title="Désactiver"
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M6.06089 4.04058C7.19005 3.29268 8.54415 2.85714 10 2.85714C13.9449 2.85714 17.1429 6.05509 17.1429 10C17.1429 11.4558 16.7073 12.81 15.9594 13.9391L6.06089 4.04058ZM4.04058 6.06089C3.29268 7.19005 2.85714 8.54415 2.85714 10C2.85714 13.9449 6.05509 17.1429 10 17.1429C11.4558 17.1429 12.81 16.7073 13.9391 15.9594L4.04058 6.06089ZM10 0C4.47714 0 0 4.47714 0 10C0 15.5229 4.47714 20 10 20C15.5229 20 20 15.5229 20 10C20 4.47714 15.5229 0 10 0Z"
                                fill="#FF6D64"
                              />
                            </svg>
                          </button>
                          <button
                            v-else
                            @click="activateUser(user.id)"
                            data-toggle="tooltip"
                            title="Activer"
                          >
                            <svg
                              width="21"
                              height="16"
                              viewBox="0 0 21 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.08424 15C7.78852 15 7.50451 14.8876 7.29478 14.6866L1.32713 8.96822C0.890957 8.55045 0.890957 7.87285 1.32713 7.45484C1.76307 7.03684 2.47035 7.03661 2.90629 7.45484L8.03838 12.3722L18.3002 1.35737C18.7108 0.916927 19.4166 0.878011 19.8769 1.27175C20.337 1.66549 20.3771 2.34195 19.9663 2.78284L8.91717 14.6424C8.7127 14.862 8.42272 14.9911 8.11577 14.9995C8.10526 14.9998 8.09475 15 8.08424 15Z"
                                fill="#6CBE3E"
                                stroke="#6CBE3E"
                                stroke-width="1.5"
                              />
                            </svg>
                          </button>
                        </div>
                      </li>
                    </ul>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <pagination
            :length="length"
            :rows="usersRows"
            :perPage="userPagedFilter.myPager.size"
            :currentPage="userPagedFilter.myPager.page"
            v-on:changePerPage="changeUserPerPage($event)"
            v-on:changeCurrentPage="changeUserCurrentPage($event)"
          ></pagination>
        </b-col>
      </b-row>
    </div>

    <user-modal
      :id="userModalId"
      :title="modalTitle"
      :description="modalDescription"
      :userForm="userForm"
      :isReadOnly="isReadOnly"
      fontSize="h3"
      backdrop-close="false"
      @confirmClick="onUserModalConfirm()"
      @closeClick="closeModalUser()"
    ></user-modal>

    <assign-user-modal
      :id="assignUserModalId"
      :userToDisable="userToDisable"
      :title="assignModalTitle"
      :description="assignModalDescription"
      :assignUserForm="assignUserForm"
      fontSize="h3"
      backdrop-close="false"
      @confirmAssignModalClick="onAssignUserModalConfirm()"
      @closeAssignModalClick="closeAssignUserModal()"
    ></assign-user-modal>
  </div>
</template>

<script>
import { userService } from "@/views/service/user.service";
import { mapActions, mapGetters } from "vuex";
import { queryConstant } from "@/shared/queryConstant";
// import FilterModal from "@/components/static/FilterModal";
import Pagination from "@/components/static/Pagination";
import userModal from "@/views/Account/UserModal";
import assignUserModal from "@/views/Account/AssignUserModal.vue";
import Actions from "../../components/static/Actions.vue";
import PageLoader from "../../components/static/pageLoader.vue";
import EmptyState from "../../components/static/emptyState.vue";
import store from "@/store";
import { constants } from "@/shared/constants";
import { cloneDeep } from "lodash";

export default {
  name: "Users",
  data: function() {
    const _pageSize_10 = 10;
    const _pageSize_25 = 25;
    const _pageSize_50 = 50;
    return {
      descIcon: require("@/assets/images/icons/desc.svg"),
      ascIcon: require("@/assets/images/icons/asc.svg"),
      userModalId: "",
      assignUserModalId: "assignUser",
      modalTitle: "",
      assignModalTitle: "",
      userToDisable: {},
      isReadOnly: false,
      roles: [],
      modalDescription: "",
      assignModalDescription: "",
      userForm: {
        lastName: "",
        firstName: "",
        email: "",
        username: "",
        phoneNumber: "",
        isEnabled: true,
        isAdmin: false,
        role: [],
        agence: {},
        agences: []
      },
      assignUserForm: {
        agence: {},
        user: {}
      },
      scrollBarSettings: {
        scrollXMarginOffset: 50
      },
      userPagedFilter: {
        myPager: {
          page: 1,
          size: 10,
          defaultSort: "id",
          defaultOrder: true
        },
        search: "",
        customFilter: queryConstant.setDefaultCustomFilter()
      },
      length: [_pageSize_10, _pageSize_25, _pageSize_50],
      usersQueryConfig: queryConstant.USERS_CONFIG,
      ROLE_TYPE: constants.ROLE_TYPE,
      USER_TYPE: constants.USER_TYPE
    };
  },
  mounted() {
    const otherQueryConfig = cloneDeep(queryConstant.USERS_CONFIG);
    if (
      this.userInfo.clientType === this.USER_TYPE.B2B2B &&
      (this.userInfo.admin ||
        this.userInfo.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE)
    ) {
      otherQueryConfig.fields.push(queryConstant.AGENCY_FILTER_OPTION);
    }
    this.usersQueryConfig = otherQueryConfig;
  },
  methods: {
    ...mapActions("notification", ["openModal", "closeModal"]),
    ...mapActions("user", [
      "changeActivation",
      "lookupUsersAction",
      "lookupUsersAgenceAction"
    ]),
    ...mapActions("agence", ["getAgenceByIdAction"]),
    fetchAgence(id) {
      this.getAgenceByIdAction(id);
    },
    updateSearchQuery(userSearch) {
      this.userPagedFilter.search = userSearch;
      this.fetchUsers();
    },
    onSubmit: function() {
      userService.addUser(this.user);
    },
    deactivateUser(user, assignedUser) {
      const isEnabled = false;
      const pagedFilter = this.userPagedFilter;
      const isAgenceTeam =
        this.$route.path.includes("agences") &&
        this.$route.path.includes("users");
      const id = user?.id;
      this.userToDisable = user;
      // In case of agence detail (users of agence)
      if (isAgenceTeam) {
        const idAgence = this.$route.params["id"];
        if (idAgence) {
          this.changeActivation({
            id,
            isEnabled,
            assignedUser: assignedUser,
            pagedFilter,
            isAgenceTeam,
            idAgence
          });
        }
      } else if (this.userInfo.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE) {
        // In case of the current user is Admin agency (users of agence)
        const currentAgence = this.userInfo.agence;
        if (currentAgence) {
          const idAgence = currentAgence.id;
          this.changeActivation({
            id,
            isEnabled,
            assignedUser: assignedUser,
            pagedFilter,
            isAgenceTeam: true,
            idAgence
          });
        }
      } else {
        // In case of the current user is Admin ( all users of the client)
        this.changeActivation({
          id,
          isEnabled,
          assignedUser: assignedUser,
          pagedFilter,
          isAgenceTeam: false,
          idAgence: null
        });
      }
    },
    activateUser(id) {
      const isEnabled = true;
      const pagedFilter = this.userPagedFilter;
      const isAgenceTeam =
        this.$route.path.includes("agences") &&
        this.$route.path.includes("users");
      // In case of agence detail (users of agence)
      if (isAgenceTeam) {
        const idAgence = this.$route.params["id"];
        if (idAgence) {
          this.changeActivation({
            id,
            isEnabled,
            assignedUser: null,
            pagedFilter,
            isAgenceTeam,
            idAgence
          });
        }
      } else if (this.userInfo.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE) {
        // In case of the current user is Admin agency (users of agence)
        const currentAgence = this.userInfo.agence;
        if (currentAgence) {
          const idAgence = currentAgence.id;
          this.changeActivation({
            id,
            isEnabled,
            assignedUser: null,
            pagedFilter,
            isAgenceTeam: true,
            idAgence
          });
        }
      } else {
        // In case of the current user is Admin ( all users of the client)
        this.changeActivation({
          id,
          isEnabled,
          assignedUser: null,
          pagedFilter,
          isAgenceTeam: false,
          idAgence: null
        });
      }
    },

    doOpenUserModal() {
      setTimeout(() => {
        this.openModal({ instance: this.$root, id: this.userModalId });
      }, 1);
    },
    doOpenAssignUserModal() {
      setTimeout(() => {
        this.openModal({ instance: this.$root, id: this.assignUserModalId });
      }, 1);
    },
    openModalUser() {
      this.userModalId = "addUser";
      this.modalTitle = "Ajouter un nouveau membre";
      this.modalDescription =
        "Ajouter un membre et attribuer les rôles et les accès";
      this.doOpenUserModal();
    },
    openAssignUserModal() {
      this.assignModalTitle = "Attribuer";
      this.assignModalDescription =
        "Des simulations et ouvrages sont attribués à ce compte. Vous devez les réattribuer à un autre utilisateur";
      this.doOpenAssignUserModal();
    },
    openModalModifUser(user) {
      this.userModalId = "modifyUser";
      this.modalTitle = "Modifier Utilisateur";
      this.userForm.id = user.id;
      this.userForm.firstName = user.firstName;
      this.userForm.lastName = user.lastName;
      this.userForm.email = user.email;
      this.userForm.phoneNumber = user.phoneNumber;
      this.userForm.isEnabled = user.isEnabled;
      this.userForm.isAdmin = user.admin;
      this.userForm.username = user.username;
      this.userForm.role = user.role;
      this.userForm.agence = user.agence;
      this.userForm.agences = user.agences;
      this.doOpenUserModal();
    },

    changeUserPerPage(perPage) {
      this.userPagedFilter.myPager.page = 1;
      this.userPagedFilter.myPager.size = perPage;

      this.fetchUsers();
    },

    closeModalUser() {
      this.closeModal({ instance: this.$root, id: this.userModalId });
      this.isReadOnly = false;
      this.userForm = {
        lastName: "",
        firstName: "",
        email: "",
        username: "",
        isEnabled: true,
        isAdmin: false,
        role: [],
        agence: {},
        agences: []
      };
    },
    closeAssignUserModal() {
      this.closeModal({ instance: this.$root, id: this.assignUserModalId });
      this.assignUserForm = {
        agence: {},
        user: {}
      };
      store.commit("user/setAffected", false);
    },
    onUsersFilter({ filter }) {
      this.userPagedFilter.customFilter = filter.custom;
      this.userPagedFilter.myPager.page = 1;
      this.fetchUsers();
    },
    reloadUsersData(filter) {
      this.onUsersFilter(filter);
    },
    sortUsersTable(name) {
      if (name === this.userPagedFilter.myPager.defaultSort) {
        this.userPagedFilter.myPager.defaultOrder = !this.userPagedFilter
          .myPager.defaultOrder;
      } else {
        this.userPagedFilter.myPager.defaultOrder = true;
      }
      this.userPagedFilter.myPager.defaultSort = name;
      this.fetchUsers();
    },
    changeUserCurrentPage(currentPage) {
      this.userPagedFilter.myPager.page = currentPage;
      this.fetchUsers();
    },
    sortUsersClass(sort) {
      return this.userPagedFilter.myPager.defaultSort === sort
        ? this.userPagedFilter.myPager.defaultOrder
          ? "asc"
          : "desc"
        : "";
    },
    fetchUsers() {
      // this.userPagedFilter = this.userSearch,
      if (this.isAgenceTeamRoute) {
        const idAgence = this.$route.params["id"];
        if (idAgence)
          this.lookupUsersAgenceAction({
            pagedFilter: this.userPagedFilter,
            idAgence: idAgence
          });
      } else if (
        this.userInfo.clientType === this.USER_TYPE.B2B2B &&
        this.userPagedFilter.customFilter.rules.some(
          rule => rule.field === "agence"
        ) &&
        (this.userInfo.admin ||
          this.userInfo.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE)
      ) {
        const agence = this.userPagedFilter.customFilter.rules.find(
          rule => rule.field === "agence"
        );
        let customFilter = cloneDeep(this.userPagedFilter.customFilter);
        customFilter.rules = this.userPagedFilter.customFilter.rules.filter(
          rule => rule.field !== "agence"
        );
        const pagedFilterClone = cloneDeep(this.userPagedFilter);
        pagedFilterClone.customFilter = customFilter;
        this.lookupUsersAgenceAction({
          pagedFilter: pagedFilterClone,
          idAgence: agence?.rules[0]?.value?.id
        });
      } else {
        this.lookupUsersAction({ pagedFilter: this.userPagedFilter });
      }
    },

    onUserModalConfirm() {
      if (this.userModalId === "modifyUser") {
        this.onModifUserModalConfirm();
      } else {
        this.onAddUserModalConfirm();
      }
    },
    onAssignUserModalConfirm() {
      this.deactivateUser(this.userToDisable, this.assignUserForm.user);
      this.closeAssignUserModal();
    },
    onAddUserModalConfirm() {
      const data = {
        lastName: this.userForm.lastName,
        firstName: this.userForm.firstName,
        email: this.userForm.email,
        username: this.userForm.username,
        phoneNumber: this.userForm.phoneNumber,
        isEnabled: true,
        admin: this.userForm.isAdmin,
        role: this.userForm.role,
        agence:
          this.userInfo.clientType === "B2B2B" &&
          this.userForm.role?.roleType !== this.ROLE_TYPE.ADMIN_AGENCE
            ? this.userForm.agence
            : null,
        agences:
          this.userInfo.clientType === "B2B2B" &&
          this.userForm.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE
            ? this.userForm.agences
            : null
      };
      if (data.admin) {
        data.role = null;
      }
      userService.addUser(data).then(() => {
        this.closeModalUser();
        this.userForm = {
          lastName: "",
          firstName: "",
          email: "",
          username: "",
          isEnabled: true,
          isAdmin: false,
          role: [],
          agence: {},
          agences: []
        };
        this.fetchUsers();
      });
    },
    onModifUserModalConfirm() {
      const data = {
        id: this.userForm.id,
        lastName: this.userForm.lastName,
        firstName: this.userForm.firstName,
        email: this.userForm.email,
        phoneNumber: this.userForm.phoneNumber,
        username: this.userForm.username,
        isEnabled: this.userForm.isEnabled,
        admin: this.userForm.isAdmin,
        role: this.userForm.role,
        agence:
          this.userInfo.clientType === "B2B2B" &&
          this.userForm.role?.roleType !== this.ROLE_TYPE.ADMIN_AGENCE
            ? this.userForm.agence
            : null,
        agences:
          this.userInfo.clientType === "B2B2B" &&
          this.userForm.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE
            ? this.userForm.agences
            : null
      };
      if (data.admin) {
        data.role = null;
      }

      userService.modifyUser(data).then(() => {
        this.closeModalUser();
        this.userForm = {
          lastName: "",
          firstName: "",
          email: "",
          username: "",
          isEnabled: true,
          isAdmin: false,
          role: [],
          agence: {},
          agences: []
        };
        this.fetchUsers();
      });
    }
  },
  components: {
    Pagination,
    userModal,
    Actions,
    PageLoader,
    EmptyState,
    assignUserModal
  },
  computed: {
    ...mapGetters("loader", {
      getActivateUserLoader: "getActivateUserLoader",
      loading: "getLoading"
    }),
    ...mapGetters("user", ["getUsers", "getAffected"]),
    ...mapGetters("account", { userInfo: "getCurrentUser" }),
    isAgenceTeamRoute() {
      return (
        this.$route.path.includes("agences") &&
        this.$route.path.includes("users")
      );
    },
    usersRows() {
      return this.getUsers.totalElements;
    }
  },
  created() {
    this.fetchUsers();
    const id = this.$route.params["id"];
    if (id) {
      this.fetchAgence(id);
    }
  },
  watch: {
    $route(to, from) {
      // when I switch from /agences/:id/users to /team using leftSideBar I get the same users of agence because it is the same vue for two routes
      this.fetchUsers();
    },
    getAffected(newValue) {
      if (newValue) {
        this.openAssignUserModal();
      }
    }
  }
};
</script>

<style scoped>
img.sort-icon {
  width: 7px;
  height: 15px;
  margin: 1px 1px 2px 7px;
  object-fit: contain;
}

th {
  cursor: pointer;
}
th:nth-child(1) {
  width: 30% !important;
}

</style>
